// Package imports:
import React, { useEffect } from "react";
import Bugsnag from "@bugsnag/js";
// Component imports:
import Modal from "../../ui-elements/Modal/Modal";
// Service imports:
import { ErrorMessages } from "../../services/errorMessages";
// Type imports:
import { IDueDiligenceModalProps, KnowYourCustomer, KnowYourIndividual, RiskScoreListInputModel } from "../../types/DueDiligenceTypes";
import { Fetched } from "../../types/Types";
import Alert from "../../ui-elements/Alert/Alert";
import Button from "../../ui-elements/Button/Button";

interface IProps extends IDueDiligenceModalProps {
    customers?: Fetched<KnowYourCustomer[]>;
    setCustomers?: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    individuals?: Fetched<KnowYourIndividual[]>;
    setIndividuals?: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
}

const AutoRiskScoreModal: React.FC<IProps> = ({
    show,
    close,
    setToastShow,
    setToastType,
    setToastMessage,
    customers,
    setCustomers,
    individuals,
    setIndividuals
}) => {
    const [disableSubmit, setDisableSubmit] = React.useState(false);
    const [checkedCustomers, setCheckedCustomers] = React.useState<KnowYourCustomer[]>([]);
    const [checkedIndividuals, setCheckedIndividuals] = React.useState<KnowYourIndividual[]>([]);

    useEffect(() => {
        if (customers instanceof Error || !customers) {
            setCheckedCustomers([]);
            return;
        }
        setCheckedCustomers(customers?.filter(c => c.checked));
    }, [customers]);

    useEffect(() => {
        if (individuals instanceof Error || !individuals) {
            setCheckedIndividuals([]);
            return;
        }
        setCheckedIndividuals(individuals?.filter(i => i.checked));
    }, [individuals]);

    const handleSubmit = async () => {
        try {
            if (customers instanceof Error || individuals instanceof Error) {
                return;
            }
            setDisableSubmit(true);

            let requestData: { riskscoreId: number | null | undefined }[] = [];

            if (customers && checkedCustomers.length > 0) {
                requestData = checkedCustomers?.map(c => ({ riskscoreId: c.riskscoreId }));
            }

            if (individuals && checkedIndividuals.length > 0) {
                requestData = checkedIndividuals?.map(i => ({ riskscoreId: i.riskscoreId }));
            }

            const response = await fetch('/DueDiligence/AutoSetRiskscores', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ riskscores: requestData })
            });

            setDisableSubmit(false);

            if (!response.ok) {
                const errorData = await response.text();
                setToastType("error");
                setToastMessage(errorData || 'Ekki tókst að skrá áhættumat');
                setToastShow(true);
                return;
            }

            const result: RiskScoreListInputModel = await response.json();

            if (result.riskscores.length > 0 && customers && setCustomers) {
                const updatedCustomers = customers.map(c => {
                    const updatedRiskScore = result.riskscores.find(r => r.customerId === c.customerId);
                    if (updatedRiskScore) {
                        return { 
                            ...c, 
                            riskscore: updatedRiskScore.riskscore, 
                            riskscoreDescription: updatedRiskScore.description,
                            status: updatedRiskScore.status ?? '',
                            statusDescription: updatedRiskScore.statusDescription ?? '',
                            checked: false
                        };
                    }
                    return {...c, checked: false };
                });
                setCustomers(updatedCustomers);
            }

            if (result.riskscores.length > 0 && individuals && setIndividuals) {
                const updatedIndividuals = individuals.map(i => {
                    const updatedRiskScore = result.riskscores.find(r => r.customerId === i.individualCustomerId);
                    if (updatedRiskScore) {
                        return {
                            ...i,
                            riskscore: updatedRiskScore.riskscore,
                            riskscoreDescription: updatedRiskScore.description,
                            status: updatedRiskScore.status ?? '',
                            statusDescription: updatedRiskScore.statusDescription ?? '',
                            checked: false
                        };
                    }
                    return {...i, checked: false };
                });
                setIndividuals(updatedIndividuals);
            }

            setToastType("success");
            setToastMessage("Áhættumati breytt");
            setToastShow(true);
            
            close();
        } catch (err) {
            setDisableSubmit(false);
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage(ErrorMessages.OtherShort);
            setToastShow(true);
        }
    }

    const renderBody = () => (
        <div className="KCL_auto-risk-score-modal">
            <div>
                <p>Áhættumat verður skráð eftir sjálfvirku mati á svörum við könnun og niðurstöðum uppflettinga. Alltaf er hægt að breyta áhættumati eftir á.</p>
                <Alert type='alert' headText='Athugaðu að sjálfvirkt áhættumat er aðeins tillaga, og því mikilvægt að yfirfara niðurstöður.' />

                <div className="form__actions">
                    <Button
                        buttonType="secondary"
                        size="lg"
                        onClick={close}
                    >
                        Hætta við
                    </Button>
                    <Button
                        size="lg"
                        onClick={handleSubmit}
                        disabled={disableSubmit}
                    >
                        Staðfesta
                    </Button>
                </div>
            </div>
        </div>
    )

    return (
        <Modal
            show={show}
            onHide={close}
            header={`Skrá sjálfvirkt áhættumat fyrir ${checkedCustomers.length} ${checkedCustomers.length.toString().endsWith("1") && !checkedCustomers.length.toString().endsWith("11") ? "félag" : "félög"}`}
            body={renderBody()}
            hasCloseButton
        />
    )
}

export default AutoRiskScoreModal;